import type { Role } from "@corefinops/schemas";
import type { Permission } from "./permissions.js";

/**
 * Role → permission bundles (Req 28/34/39). Roles are STABLE bundles; there are
 * no customer-defined custom roles in v1. This mapping is the single source of
 * authorization truth for what a role can do and is unit-tested.
 *
 * A user may hold multiple roles; effective permissions are the union
 * (see computeEffectivePermissions).
 */

// Read-only baseline shared by most roles.
const VIEWER_READS: readonly Permission[] = [
  "overview.read",
  "findings.read",
  "estate.read",
  "portfolio.read",
  "assessment.read",
  "readiness.read",
  "wave.read",
  "dualrun.read",
  "decommission.read",
  "recommendation.read",
  "action.read",
  "ledger.read",
  "explorer.read",
  "report.read",
  "budget.read",
  "metadata.read",
  "aws_connection.read",
  "ingestion.read",
  "notification.read",
];

export const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = {
  // Full administrative control within the tenant.
  org_admin: [
    ...VIEWER_READS,
    "estate.write",
    "portfolio.write",
    "import.write",
    "import.read",
    "assessment.write",
    "readiness.write",
    "wave.write",
    "cutover.write",
    "decommission.write",
    "action.write",
    "action.approve",
    "exception.write",
    "ledger.verify",
    "budget.write",
    "explorer.write",
    "aws_connection.write",
    "job.run",
    "tenant.admin",
    "user.admin",
    "audit.read",
    "billing.read",
    "billing.manage",
    "support.request",
  ],

  // Executive: read-focused control-tower view (Req 39 Executive).
  executive: [...VIEWER_READS, "audit.read", "billing.read"],

  // FinOps lead: explorer, app economics, recommendations, actions, budgets,
  // and value verification (Req 39 FinOps Lead).
  finops_lead: [
    ...VIEWER_READS,
    "import.read",
    "action.write",
    "budget.write",
    "explorer.write",
    "exception.write",
    "ledger.verify",
    "job.run",
    "support.request",
  ],

  // Migration lead: portfolio, readiness, waves, cutover, decommission (Req 39).
  migration_lead: [
    ...VIEWER_READS,
    "portfolio.write",
    "import.read",
    "import.write",
    "assessment.write",
    "readiness.write",
    "wave.write",
    "cutover.write",
    "decommission.write",
    "action.write",
    "support.request",
  ],

  // Platform operations: actions, optimization, lifecycle, backup economics.
  platform_ops: [...VIEWER_READS, "action.write", "aws_connection.write", "ingestion.read", "job.run"],

  // Application owner: their apps/migration/recommendations/approvals/actions.
  app_owner: [...VIEWER_READS, "action.write", "action.approve", "exception.write"],

  // Finance: baseline, TCO, budgets, verified value, reports (Req 39 Finance).
  finance: [...VIEWER_READS, "budget.write", "ledger.verify", "action.approve", "billing.read", "support.request"],

  // Read-only.
  viewer: [...VIEWER_READS],
};
